import { useSuspenseQuery } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Distribution } from './-components/shared/distribution'
import { DistributionPending } from './-components/shared/distribution-pending'
import { useReport } from '@/hooks/use-report'
import { ReportError } from './-components/shared/report-error'
import { EmptyState } from './-components/shared/empty-state'

export const Route = createFileRoute('/_app/teams/$teamId/distribution')({
  component: RouteComponent,
  pendingComponent: DistributionPending,
  // Shown immediately: at the router's default pendingMs the NAV STACK cannot push while the loaders run,
  // because the level it would push is the route being rendered — see the full story in jobs.$jobId.report.
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const { report } = useReport(context.reportQueryOptions)
  // The layout above declares and warms this; deferring the OBJECT (stable per match) keeps the
  // previous team on screen while a filter/type change loads.
  const deferredTeam = useDeferredValue(context.teamQueryOptions)
  const { data: team } = useSuspenseQuery(deferredTeam)

  // `visibleMemberIds` is already restricted to the selected self-assessments for `ownsManual`
  // (server-side); it carries ids rather than a second copy of the member objects, so select from
  // `members`, whose order (ranked by match percentage) is the order to keep.
  const visibleIds = new Set(team?.visibleMemberIds ?? [])
  const members = (team?.members ?? [])
    .filter((member) => visibleIds.has(member.id))
    .map((member) => ({ ...member, id: member.id.toString() }))

  if (team?.error === 'teams-see-owns') {
    return <ReportError error="noPermissionsSeeOwns" surface="distribution" />
  }

  // Members with no results, including a half-registered roster: the one notice (a residual 'profile-blocked' too).
  if (team?.emptyStateCode || team?.error) {
    return <EmptyState code={team.emptyStateCode ?? 'noMemberResults'} surface="distribution" />
  }

  return <Distribution report={report} profiles={members} data={team.distribution} showFilters />
}
